import React, { useState, useEffect } from 'react';
import { FaHistory, FaSearch, FaUser } from 'react-icons/fa';
import { FiActivity } from 'react-icons/fi';
import { fetchAPI } from '../../utils/api';

export default function AdminHistory() {
    const [logs, setLogs] = useState([]);
    const [pagination, setPagination] = useState({ page: 1, pages: 1 });
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('');
    const [currentPage, setCurrentPage] = useState(1);

    useEffect(() => {
        const loadLogs = async () => {
            try {
                setLoading(true);
                const data = await fetchAPI(`/admin/get_activity_log.php?page=${currentPage}&limit=15`);
                setLogs(data.logs);
                setPagination(data.pagination);
            } catch (err) {
                console.error("Failed to load activity logs", err);
            } finally {
                setLoading(false);
            }
        };
        loadLogs();
    }, [currentPage]);

    // Client-side filtering (applies to the current page only for better performance)
    const filteredLogs = logs.filter(log =>
        (log.user_name?.toLowerCase() || 'system').includes(filter.toLowerCase()) ||
        log.action.toLowerCase().includes(filter.toLowerCase()) ||
        log.details?.toLowerCase().includes(filter.toLowerCase())
    );

    const getActionColor = (action) => {
        if (action.includes('LOGIN')) return 'bg-blue-100 text-blue-700';
        if (action.includes('APPROVED') || action.includes('CREATED')) return 'bg-green-100 text-green-700';
        if (action.includes('REJECTED') || action.includes('DELETED')) return 'bg-red-100 text-red-700';
        return 'bg-gray-100 text-gray-700';
    };

    const formatDevice = (ua) => {
        if (!ua) return 'Unknown';
        if (ua.includes('Windows')) return 'Windows PC';
        if (ua.includes('iPhone')) return 'iPhone';
        if (ua.includes('Android')) return 'Android Device';
        if (ua.includes('Macintosh')) return 'MacBook';
        return 'Other Device';
    };

    return (
        <div className="space-y-6">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 bg-white/60 backdrop-blur-xl rounded-xl p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-white/50">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 tracking-tight font-display mb-2 flex items-center gap-3">
                        <FaHistory className="text-blue-600" /> System Activity Log
                    </h1>
                    <p className="text-gray-500 font-medium">Track all system activities and security events.</p>
                </div>
                <div className="relative w-full md:w-80">
                    <input
                        type="text"
                        placeholder="Search current page..."
                        value={filter}
                        onChange={(e) => setFilter(e.target.value)}
                        className="w-full pl-10 pr-4 py-2.5 border border-white/50 rounded-xl bg-white/50 focus:bg-white focus:ring-2 focus:ring-blue-500 outline-none font-medium transition-all"
                    />
                    <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-100">
                        <thead>
                            <tr className="bg-gray-50">
                                <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-widest">User</th>
                                <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-widest">Action</th>
                                <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-widest">Details</th>
                                <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-widest">Device</th>
                                <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-widest">IP Address</th>
                                <th className="px-6 py-4 text-right text-xs font-bold text-gray-400 uppercase tracking-widest">Timestamp</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-50">
                            {loading ? (
                                <tr>
                                    <td colSpan="6" className="px-6 py-12 text-center text-gray-400 font-medium italic italic animate-pulse">
                                        Loading activity logs...
                                    </td>
                                </tr>
                            ) : filteredLogs.length === 0 ? (
                                <tr>
                                    <td colSpan="6" className="px-6 py-12 text-center text-gray-400 font-medium italic">No activity logs found</td>
                                </tr>
                            ) : filteredLogs.map((log) => (
                                <tr key={log.id} className="hover:bg-gray-50/50 transition-colors">
                                    <td className="px-6 py-4">
                                        <div className="flex items-center gap-3">
                                            <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-400">
                                                <FaUser size={12} />
                                            </div>
                                            <div>
                                                <div className="text-sm font-bold text-gray-900">{log.user_name || 'System'}</div>
                                                <div className="text-[10px] text-gray-400 font-black uppercase tracking-wider">{log.user_role || 'N/A'}</div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-wider ${getActionColor(log.action)}`}>
                                            {log.action.replace(/_/g, ' ')}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="text-xs text-gray-600 font-medium max-w-xs truncate" title={log.details}>
                                            {log.details || 'No additional details'}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="text-[10px] font-bold text-gray-500 uppercase flex items-center gap-1">
                                            <FiActivity size={10} className="text-gray-300" /> {formatDevice(log.user_agent)}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="text-[10px] font-mono text-gray-400">{log.ip_address}</div>
                                    </td>
                                    <td className="px-6 py-4 text-right">
                                        <div className="text-xs text-gray-500 font-medium">
                                            {new Date(log.created_at).toLocaleString()}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Pagination Controls */}
                <div className="px-6 py-4 bg-gray-50/50 border-t border-gray-100 flex items-center justify-between">
                    <div className="text-xs text-gray-500 font-bold uppercase tracking-wider">
                        Page <span className="text-blue-600">{pagination.page}</span> of <span className="text-blue-600">{pagination.pages}</span>
                        <span className="ml-4 text-gray-300">|</span>
                        <span className="ml-4">Total Activities: {pagination.total}</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                            disabled={currentPage === 1 || loading}
                            className="px-4 py-2 text-xs font-black uppercase tracking-widest bg-white border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                        >
                            Previous
                        </button>
                        {[...Array(pagination.pages)].map((_, i) => {
                            const pageNum = i + 1;
                            // Only show current page, first, last, and pages around current
                            if (
                                pageNum === 1 ||
                                pageNum === pagination.pages ||
                                (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
                            ) {
                                return (
                                    <button
                                        key={pageNum}
                                        onClick={() => setCurrentPage(pageNum)}
                                        className={`w-8 h-8 flex items-center justify-center rounded-lg text-xs font-bold transition-all ${currentPage === pageNum
                                            ? 'bg-blue-600 text-white border-blue-600'
                                            : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
                                            }`}
                                    >
                                        {pageNum}
                                    </button>
                                );
                            } else if (
                                pageNum === currentPage - 2 ||
                                pageNum === currentPage + 2
                            ) {
                                return <span key={pageNum} className="text-gray-400 text-xs">...</span>;
                            }
                            return null;
                        })}
                        <button
                            onClick={() => setCurrentPage(prev => Math.min(pagination.pages, prev + 1))}
                            disabled={currentPage === pagination.pages || loading}
                            className="px-4 py-2 text-xs font-black uppercase tracking-widest bg-white border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                        >
                            Next
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
